<template>
  <Layout>
    <div class="integration-clients">
      <header class="clients-hero">
        <div>
          <div class="hero-eyebrow">Painel de integração</div>
          <h1>Gestão de clientes vinculados</h1>
          <p>
            Acompanhe clientes conectados, status de sincronização e pendências
            em uma única tela.
          </p>
        </div>
        <div class="hero-actions">
          <button class="btn btn-light" type="button" @click="clearFilters">
            <i class="uil uil-filter-slash"></i>
            Limpar filtros
          </button>
          <button class="btn btn-webreceita" type="button" @click="refreshPanel">
            <i class="uil uil-refresh"></i>
            Atualizar painel
          </button>
        </div>
      </header>

      <section class="manager-card">
        <div class="toolbar">
          <div class="search-box">
            <i class="uil uil-search"></i>
            <input
              v-model="search"
              type="text"
              placeholder="Buscar cliente, documento ou ambiente"
            />
          </div>
          <button class="btn btn-webreceita toolbar-search-button" type="button" @click="searchClients">
            <i class="uil uil-search"></i>
            Pesquisar
          </button>
        </div>

        <div class="content-grid">
          <div class="client-list">
            <button
              v-for="client in filteredClients"
              :key="client.id"
              :class="['client-row', { active: selectedClient && selectedClient.id === client.id }]"
              type="button"
              @click="selectClient(client)"
            >
              <span :class="['client-status-dot', client.status]"></span>
              <span class="client-main">
                <strong>{{ client.nome }}</strong>
                <small>{{ client.documento }} - {{ client.integracao }}</small>
              </span>
              <span :class="['client-status-pill', client.status]">
                {{ statusLabel(client.status) }}
              </span>
            </button>

            <div v-if="!filteredClients.length" class="empty-state">
              Nenhum cliente encontrado para os filtros selecionados.
            </div>
          </div>

          <aside class="detail-panel">
            <b-overlay
              :show="isDetailLoading"
              rounded="sm"
              opacity="0.78"
              spinner-variant="success"
            >
              <template v-if="selectedClient">
              <div class="detail-head">
                <span class="detail-avatar">{{ initials(selectedClient.nome) }}</span>
                <div>
                  <h2>{{ selectedClient.nome }}</h2>
                  <p>{{ selectedClient.documento }}</p>
                </div>
              </div>

              <nav class="client-tabs">
                <button
                  v-for="tab in tabs"
                  :key="tab.value"
                  :class="{ active: activeTab === tab.value }"
                  type="button"
                  @click="activeTab = tab.value"
                >
                  <i :class="tab.icon"></i>
                  {{ tab.label }}
                </button>
              </nav>

              <section v-if="activeTab === 'overview'" class="tab-panel">
                <div class="detail-grid">
                  <div>
                    <span>Integração</span>
                    <strong>{{ selectedClient.integracao || '-' }}</strong>
                  </div>
                  <div>
                    <span>Ambiente</span>
                    <strong>{{ clientSettings.ambiente === 'homologacao' ? 'Homologação' : 'Produção' }}</strong>
                  </div>                  
                </div>

                <div class="health-box">
                  <div>
                    <span>Status da integração</span>
                    <strong>{{ statusLabel(selectedClient.status) }}</strong>
                  </div>
                  <div class="health-bar">
                    <span :style="{ width: selectedClient.status === 'erro' ? '35%' : selectedClient.status === 'pendente' ? '68%' : '100%' }"></span>
                  </div>
                </div>

                <div class="pending-box">
                  <h3>Indicadores do cliente</h3>
                  <ul>
                    <li>{{ selectedClient.totalProdutores || 0 }} produtores vinculados.</li>
                    <li>{{ selectedClient.totalResponsaveis || 0 }} responsáveis técnicos.</li>
                    <li>{{ selectedClient.totalReceitas || 0 }} receitas emitidas.</li>
                  </ul>
                </div>
              </section>

              <section v-if="activeTab === 'producers'" class="tab-panel">
                <div class="panel-head">
                  <div>
                    <h3>Produtores</h3>
                    <p>Cadastros do cliente selecionado.</p>
                  </div>
                </div>
                <div class="producer-toolbar">
                  <div class="search-box">
                    <i class="uil uil-search"></i>
                    <input
                      v-model="producerSearch"
                      :disabled="isDetailLoading"
                      type="text"
                      placeholder="Pesquisar produtor por nome"
                    />
                  </div>
                </div>
                <div class="mini-table">
                  <div
                    v-for="producer in clientProducers"
                    :key="producer.id"
                    class="mini-row person-row"
                  >
                    <div>
                      <strong>{{ producer.nome }}</strong>
                      <span>{{ producer.documento || '-' }}</span>
                      <small>{{ [producer.propriedade, producer.cidade, producer.estado].filter(Boolean).join(' - ') || '-' }}</small>
                    </div>
                    <button
                      class="btn btn-light btn-row-action"
                      type="button"
                      @click="openProducerDetails(producer)"
                    >
                      <i class="uil uil-eye"></i>
                      Detalhes
                    </button>
                  </div>
                  <div v-if="!clientProducers.length" class="mini-empty">
                    Nenhum produtor encontrado para este cliente.
                  </div>
                </div>
                <div v-if="producersTotal > producersPage.take" class="mini-pagination">
                  <span>
                    Página {{ producersCurrentPage }} de {{ producersTotalPages }}
                    - {{ producersTotal }} produtores
                  </span>
                  <div>
                    <button
                      class="btn btn-light"
                      type="button"
                      :disabled="producersCurrentPage <= 1 || isDetailLoading"
                      @click="changeProducersPage(producersCurrentPage - 1)"
                    >
                      <i class="uil uil-angle-left"></i>
                      Anterior
                    </button>
                    <button
                      class="btn btn-light"
                      type="button"
                      :disabled="producersCurrentPage >= producersTotalPages || isDetailLoading"
                      @click="changeProducersPage(producersCurrentPage + 1)"
                    >
                      Próxima
                      <i class="uil uil-angle-right"></i>
                    </button>
                  </div>
                </div>
              </section>

              <section v-if="activeTab === 'technicians'" class="tab-panel">
                <div class="panel-head">
                  <div>
                    <h3>Responsáveis técnicos</h3>
                    <p>Profissionais vinculados ao cliente.</p>
                  </div>
                </div>
                <div class="producer-toolbar">
                  <div class="search-box">
                    <i class="uil uil-search"></i>
                    <input
                      v-model="technicianSearch"
                      :disabled="isDetailLoading"
                      type="text"
                      placeholder="Pesquisar responsável por nome"
                    />
                  </div>
                </div>
                <div class="mini-table">
                  <div
                    v-for="technician in filteredTechnicians"
                    :key="technician.id"
                    class="mini-row person-row"
                  >
                    <div>
                      <strong>{{ technician.nome }}</strong>
                      <span>{{ technician.crea || technician.documento || '-' }}</span>
                      <small>{{ technician.email || technician.telefone || '-' }}</small>
                    </div>
                    <button
                      class="btn btn-light btn-row-action"
                      type="button"
                      @click="openTechnicianDetails(technician)"
                    >
                      <i class="uil uil-eye"></i>
                      Detalhes
                    </button>
                  </div>
                  <div v-if="!filteredTechnicians.length" class="mini-empty">
                    Nenhum responsável técnico encontrado para este cliente.
                  </div>
                </div>
              </section>

              <section v-if="activeTab === 'recipes'" class="tab-panel">
                <div class="panel-head">
                  <div>
                    <h3>Receitas emitidas</h3>
                    <p>Últimas emissões feitas pelo cliente.</p>
                  </div>
                </div>
                <div class="mini-table">
                  <div
                    v-for="recipe in clientPrescriptions"
                    :key="recipe.id"
                    class="mini-row recipe-row"
                  >
                    <strong>{{ recipe.numero || ('#' + recipe.id) }}</strong>
                    <span>{{ recipe.produtor || '-' }}</span>
                    <small>{{ formatDate(recipe.dataEmissao) }} - {{ recipe.status || '-' }}</small>
                  </div>
                  <div v-if="!clientPrescriptions.length" class="mini-empty">
                    Nenhuma receita encontrada para este cliente.
                  </div>
                </div>
              </section>

              <section v-if="activeTab === 'settings'" class="tab-panel">
                <div class="panel-head">
                  <div>
                    <h3>Configurações do cliente</h3>
                    <p>Logo e dados de recebimento usados nas emissões.</p>
                  </div>
                </div>

                <div class="logo-upload-card">
                  <div class="logo-preview">
                    <img
                      v-if="logoPreview"
                      :src="logoPreview"
                      alt="Logo do cliente"
                    />
                    <span v-else>{{ initials(selectedClient.nome) }}</span>
                  </div>
                  <div>
                    <strong>Logo da empresa</strong>
                    <p>
                      Essa configuração já existe por cliente no sistema. Aqui ela fica
                      centralizada para a empresa integradora operar.
                    </p>
                    <label class="logo-upload-button">
                      <i class="uil uil-image-upload"></i>
                      Enviar nova logo
                      <input accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" type="file" @change="handleLogoUpload" />
                    </label>
                  </div>
                </div>

                <div class="settings-grid">
                  <label class="settings-wide">
                    Unidade de Recebimento das Embalagens
                    <input
                      v-model="clientSettings.unidadeRecebimentoEmbalagens"
                      type="text"
                      placeholder="Unidade de Recebimento das Embalagens"
                    />
                  </label>
                </div>

                <div class="detail-actions">
                  <button class="btn btn-webreceita" type="button" @click="saveSettings">
                    <i class="uil uil-save"></i>
                    Salvar configurações
                  </button>
                </div>
              </section>
              </template>
              <div v-else class="empty-state">
                Selecione um cliente vinculado para visualizar os dados.
              </div>
            </b-overlay>
          </aside>
        </div>
      </section>

      <b-modal
        v-model="detailModal.visible"
        centered
        hide-footer
        size="lg"
        :title="detailModal.title"
      >
        <b-overlay
          :show="detailModal.loading"
          rounded="sm"
          opacity="0.78"
          spinner-variant="success"
        >
          <div v-if="detailModal.item" class="record-detail-modal">
            <div class="record-detail-head">
              <span class="detail-avatar">{{ initials(detailModal.item.nome) }}</span>
              <div>
                <h3>{{ detailModal.item.nome || '-' }}</h3>
                <p>{{ detailModal.subtitle }}</p>
              </div>
            </div>

            <div class="record-summary-strip">
              <div
                v-for="field in detailModalSummaryFields"
                :key="field.label"
              >
                <span>{{ field.label }}</span>
                <strong>{{ field.value }}</strong>
              </div>
            </div>

            <div class="record-sections">
              <section
                v-for="section in detailModalSections"
                :key="section.title"
                class="record-section"
              >
                <h4>{{ section.title }}</h4>
                <dl>
                  <template v-for="field in section.fields">
                    <dt :key="field.key + '-label'">{{ field.label }}</dt>
                    <dd :key="field.key + '-value'">{{ field.value }}</dd>
                  </template>
                </dl>
              </section>
            </div>
          </div>
        </b-overlay>
      </b-modal>

      <b-overlay :show="isLoading" no-wrap />
    </div>
  </Layout>
</template>

<script>
import Layout from '@layouts/main'
import integrationClientsService from '@/src/services/integracao-clientes'

export default {
  components: {
    Layout,
  },
  data() {
    return {
      isLoading: false,
      isDetailLoading: false,
      search: '',
      statusFilter: 'todos',
      integrationFilter: 'todos',
      filterDebounce: null,
      producerSearch: '',
      producerSearchDebounce: null,
      technicianSearch: '',
      activeTab: 'overview',
      selectedClient: null,
      summary: {
        totalClientes: 0,
        sincronizados: 0,
        pendentes: 0,
        comErro: 0,
      },
      page: {
        skip: 0,
        take: 20,
        current: 1,
        total_pages: 1,
      },
      totalClients: 0,
      clientProducers: [],
      producersTotal: 0,
      producersPage: {
        skip: 0,
        take: 5,
      },
      clientTechnicians: [],
      clientPrescriptions: [],
      detailModal: {
        visible: false,
        loading: false,
        type: '',
        title: '',
        subtitle: '',
        item: null,
      },
      clientSettings: {
        ambiente: 'producao',
        sincronizacaoAtiva: true,
        emitirReceitasAutomaticamente: false,
        exigirResponsavelTecnico: false,
        observacoes: '',
        unidadeRecebimentoEmbalagens: '',
      },
      logoPreview: '',
      tabs: [
        { value: 'overview', label: 'Resumo', icon: 'uil uil-chart' },
        { value: 'producers', label: 'Produtores', icon: 'uil uil-users-alt' },
        { value: 'technicians', label: 'Responsáveis', icon: 'uil uil-user-md' },
        { value: 'recipes', label: 'Receitas', icon: 'uil uil-clipboard-notes' },
        { value: 'settings', label: 'Configurações', icon: 'uil uil-setting' },
      ],
      clients: [],
    }
  },
  computed: {
    token() {
      return this.$store.state.auth.currentUser.token
    },
    filteredClients() {
      return this.clients
    },
    syncedCount() {
      return this.summary.sincronizados
    },
    warningCount() {
      return this.summary.pendentes
    },
    errorCount() {
      return this.summary.comErro
    },
    producersCurrentPage() {
      return Math.floor(this.producersPage.skip / this.producersPage.take) + 1
    },
    producersTotalPages() {
      return Math.max(1, Math.ceil(this.producersTotal / this.producersPage.take))
    },
    filteredTechnicians() {
      const search = String(this.technicianSearch || '').toLowerCase().trim()

      if (!search) {
        return this.clientTechnicians
      }

      return this.clientTechnicians.filter((technician) =>
        [
          technician.nome,
          technician.documento,
          technician.crea,
          technician.email,
          technician.telefone,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(search)
      )
    },
    detailModalFields() {
      const item = this.detailModal.item || {}
      const preferredOrder =
        this.detailModal.type === 'producer'
          ? [
              'nome',
              'documento',
              'cpf',
              'cnpj',
              'cpfCnpj',
              'cpf_cnpj',
              'inscricaoEstadual',
              'inscricao_estadual',
              'propriedade',
              'endereco',
              'logradouro',
              'numero',
              'complemento',
              'bairro',
              'cep',
              'cidade',
              'municipio',
              'estado',
              'uf',
              'email',
              'telefone',
              'status',
              'ativo',
              'totalReceitas',
            ]
          : [
              'nome',
              'documento',
              'cpf',
              'crea',
              'profissao',
              'email',
              'telefone',
              'cep',
              'logradouro',
              'numero',
              'complemento',
              'bairro',
              'cidade',
              'municipio',
              'estado',
              'uf',
              'status',
              'ativo',
              'n_receitas_art',
              'nReceitasArt',
              'nReceitasMes',
              'receita_art_disponivel',
              'receitaArtDisponivel',
              'totalReceitas',
            ]

      return Object.entries(item)
        .filter(([key, value]) => this.shouldShowDetailField(key, value, item))
        .sort(([keyA], [keyB]) => {
          const indexA = preferredOrder.indexOf(keyA)
          const indexB = preferredOrder.indexOf(keyB)

          if (indexA !== -1 && indexB !== -1) return indexA - indexB
          if (indexA !== -1) return -1
          if (indexB !== -1) return 1

          return keyA.localeCompare(keyB)
        })
        .map(([key, value]) => ({
          key,
          label: this.fieldLabel(key),
          value: this.formatDetailValue(key, value),
        }))
    },
    detailModalSummaryFields() {
      const keys =
        this.detailModal.type === 'producer'
          ? ['documento', 'cpfCnpj', 'cpf_cnpj', 'telefone', 'status', 'ativo']
          : ['cpf', 'documento', 'crea', 'profissao', 'telefone', 'status', 'ativo']

      const fields = []

      keys.forEach((key) => {
        const field = this.detailModalFields.find((item) => item.key === key)
        if (field && !fields.some((item) => item.label === field.label)) {
          fields.push(field)
        }
      })

      return fields.slice(0, 4)
    },
    detailModalSections() {
      const summaryKeys = this.detailModalSummaryFields.map((field) => field.key)
      const fields = this.detailModalFields.filter(
        (field) => field.key !== 'nome' && !summaryKeys.includes(field.key)
      )
      const groups =
        this.detailModal.type === 'producer'
          ? [
              {
                title: 'Contato e localização',
                keys: [
                  'email',
                  'telefone',
                  'cep',
                  'endereco',
                  'logradouro',
                  'numero',
                  'complemento',
                  'bairro',
                  'cidade',
                  'municipio',
                  'estado',
                  'uf',
                  'localAplicacao',
                  'local_aplicacao',
                  'localidade',
                  'propriedade',
                ],
              },
            ]
          : [
              {
                title: 'Dados técnicos',
                keys: [
                  'documento',
                  'cpf',
                  'crea',
                  'profissao',
                  'status',
                  'ativo',
                  'n_receitas_art',
                  'nReceitasArt',
                  'nReceitasMes',
                  'receita_art_disponivel',
                  'receitaArtDisponivel',
                  'totalReceitas',
                  'buscaArt',
                  'artPrimaria',
                  'artSecundaria',
                  'formatoReceita',
                  'nCopias',
                  'registroSc',
                  'registroEmpresaSc',
                  'digitalSignature',
                  'isSignProd',
                  'data_cadastro',
                  'dataCadastro',
                  'data_atualizacao',
                  'dataAtualizacao',
                  'ultima_receita',
                  'ultimaReceita',
                ],
              },
              {
                title: 'Contato e localização',
                keys: [
                  'email',
                  'telefone',
                  'cep',
                  'logradouro',
                  'numero',
                  'complemento',
                  'bairro',
                  'cidade',
                  'municipio',
                  'estado',
                  'uf',
                ],
              },
            ]

      const usedKeys = new Set()
      const sections = groups
        .map((group) => {
          const sectionFields = fields.filter((field) => group.keys.includes(field.key))
          sectionFields.forEach((field) => usedKeys.add(field.key))

          return {
            title: group.title,
            fields: sectionFields,
          }
        })
        .filter((section) => section.fields.length)

      const otherFields =
        this.detailModal.type === 'producer'
          ? []
          : fields.filter((field) => !usedKeys.has(field.key))
      if (otherFields.length) {
        sections.push({
          title: 'Outras informações',
          fields: otherFields,
        })
      }

      return sections
    },
  },
  mounted() {
    this.refreshPanel(false)
  },
  watch: {
    statusFilter() {
      this.queueFilterRefresh()
    },
    integrationFilter() {
      this.queueFilterRefresh()
    },
    producerSearch() {
      this.queueProducerSearch()
    },
  },
  methods: {
    async selectClient(client) {
      this.selectedClient = client
      this.activeTab = 'overview'
      this.producerSearch = ''
      this.technicianSearch = ''
      this.resetProducersPagination()
      await this.loadClientDetails(client.id)
    },
    async refreshPanel(showSuccess = true) {
      this.isLoading = true

      try {
        await Promise.all([this.loadSummary(), this.loadClients()])

        if (showSuccess) {
          this.showToast('Painel atualizado.', 'success')
        }
      } catch (error) {
        this.showToast(this.getErrorMessage(error), 'danger')
      } finally {
        this.isLoading = false
      }
    },
    clearFilters() {
      this.search = ''
      this.statusFilter = 'todos'
      this.integrationFilter = 'todos'
      this.loadClients()
    },
    searchClients() {
      this.page.skip = 0
      this.loadClients()
    },
    queueFilterRefresh() {
      window.clearTimeout(this.filterDebounce)
      this.filterDebounce = window.setTimeout(() => {
        this.loadClients()
      }, 400)
    },
    queueProducerSearch() {
      window.clearTimeout(this.producerSearchDebounce)
      this.producerSearchDebounce = window.setTimeout(() => {
        this.resetProducersPagination()
        this.refreshClientProducers()
      }, 400)
    },
    async loadSummary() {
      const response = await integrationClientsService.getIntegrationClientsSummary(this.token)
      this.summary = {
        totalClientes: Number(response.data.totalClientes || 0),
        sincronizados: Number(response.data.sincronizados || 0),
        pendentes: Number(response.data.pendentes || 0),
        comErro: Number(response.data.comErro || 0),
      }
    },
    async loadClients() {
      const response = await integrationClientsService.listIntegrationClients(this.token, {
        search: this.search,
        status: this.statusFilter,
        integracao: this.integrationFilter,
        skip: this.page.skip,
        take: this.page.take,
      })
      const clients = Array.isArray(response.data.clientes) ? response.data.clientes : []
      this.clients = clients
      this.totalClients = Number(response.data.total || response.data.count || clients.length)
      this.page = response.data.page || this.page

      if (!clients.length) {
        this.selectedClient = null
        this.resetClientDetails()
        return
      }

      const selectedStillVisible =
        this.selectedClient && clients.some((client) => client.id === this.selectedClient.id)
      const nextClient = selectedStillVisible
        ? clients.find((client) => client.id === this.selectedClient.id)
        : clients[0]

      if (!selectedStillVisible) {
        this.producerSearch = ''
        this.technicianSearch = ''
        this.resetProducersPagination()
      }

      this.selectedClient = nextClient
      await this.loadClientDetails(nextClient.id)
    },
    async loadClientDetails(clienteId) {
      if (!clienteId) return

      this.isDetailLoading = true

      try {
        const [
          detailResponse,
          techniciansResponse,
          prescriptionsResponse,
          settingsResponse,
        ] = await Promise.all([
          integrationClientsService.getIntegrationClient(this.token, clienteId),
          integrationClientsService.listIntegrationClientTechnicians(this.token, clienteId),
          integrationClientsService.listIntegrationClientPrescriptions(this.token, clienteId, {
            take: 100,
          }),
          integrationClientsService.getIntegrationClientSettings(this.token, clienteId),
        ])

        if (detailResponse.data && detailResponse.data.cliente) {
          this.selectedClient = {
            ...this.selectedClient,
            ...detailResponse.data.cliente,
          }
        }

        await this.loadClientProducers(clienteId)
        this.clientTechnicians = Array.isArray(techniciansResponse.data.responsaveis)
          ? techniciansResponse.data.responsaveis
          : []
        this.clientPrescriptions = Array.isArray(prescriptionsResponse.data.receitas)
          ? prescriptionsResponse.data.receitas
          : []

        const settings = settingsResponse.data.configuracoes || {}
        this.clientSettings = {
          ambiente: settings.ambiente || 'producao',
          sincronizacaoAtiva:
            settings.sincronizacaoAtiva === undefined ? true : Boolean(settings.sincronizacaoAtiva),
          emitirReceitasAutomaticamente: Boolean(settings.emitirReceitasAutomaticamente),
          exigirResponsavelTecnico: Boolean(settings.exigirResponsavelTecnico),
          observacoes: settings.observacoes || '',
          unidadeRecebimentoEmbalagens: settings.unidadeRecebimentoEmbalagens || '',
        }
        this.logoPreview =
          settingsResponse.data.logoUrl ||
          (this.selectedClient && this.selectedClient.logoUrl) ||
          ''
      } catch (error) {
        this.showToast(this.getErrorMessage(error), 'danger')
        this.resetClientDetails(false)
      } finally {
        this.isDetailLoading = false
      }
    },
    async loadClientProducers(clienteId) {
      const response = await integrationClientsService.listIntegrationClientProducers(
        this.token,
        clienteId,
        {
          search: this.producerSearch,
          skip: this.producersPage.skip,
          take: this.producersPage.take,
        }
      )

      this.clientProducers = Array.isArray(response.data.produtores)
        ? response.data.produtores
        : []
      this.producersTotal = Number(response.data.total || this.clientProducers.length)
    },
    async changeProducersPage(page) {
      if (!this.selectedClient) return

      const nextPage = Math.min(Math.max(Number(page) || 1, 1), this.producersTotalPages)
      this.producersPage.skip = (nextPage - 1) * this.producersPage.take
      this.isDetailLoading = true

      try {
        await this.loadClientProducers(this.selectedClient.id)
      } catch (error) {
        this.showToast(this.getErrorMessage(error), 'danger')
      } finally {
        this.isDetailLoading = false
      }
    },
    async refreshClientProducers() {
      if (!this.selectedClient) return

      this.isDetailLoading = true

      try {
        await this.loadClientProducers(this.selectedClient.id)
      } catch (error) {
        this.showToast(this.getErrorMessage(error), 'danger')
      } finally {
        this.isDetailLoading = false
      }
    },
    resetProducersPagination() {
      this.producersPage.skip = 0
      this.producersTotal = 0
    },
    async openProducerDetails(producer) {
      this.detailModal = {
        visible: true,
        loading: true,
        type: 'producer',
        title: 'Detalhes do produtor',
        subtitle: producer.documento || 'Cadastro de produtor',
        item: producer,
      }

      if (!this.selectedClient) {
        this.detailModal.loading = false
        return
      }

      try {
        const response = await integrationClientsService.getIntegrationClientProducer(
          this.token,
          this.selectedClient.id,
          this.getRecordId(producer, ['id', 'id_produtor_rural', 'produtorId'])
        )
        const detail = this.extractDetailItem(response.data, [
          'produtor',
          'produtorRural',
          'cliente',
          'data',
        ])

        this.detailModal.item = {
          ...producer,
          ...detail,
        }
        this.detailModal.subtitle =
          this.detailModal.item.documento ||
          this.detailModal.item.cpfCnpj ||
          this.detailModal.item.cpf_cnpj ||
          'Cadastro de produtor'
      } catch (error) {
        this.showToast(this.getErrorMessage(error), 'warning')
      } finally {
        this.detailModal.loading = false
      }
    },
    async openTechnicianDetails(technician) {
      this.detailModal = {
        visible: true,
        loading: true,
        type: 'technician',
        title: 'Detalhes do responsável técnico',
        subtitle: technician.crea || technician.documento || 'Cadastro de responsável técnico',
        item: technician,
      }

      if (!this.selectedClient) {
        this.detailModal.loading = false
        return
      }

      try {
        const response = await integrationClientsService.getIntegrationClientTechnician(
          this.token,
          this.selectedClient.id,
          this.getRecordId(technician, ['id', 'id_agronomo', 'responsavelId'])
        )
        const detail = this.extractDetailItem(response.data, [
          'responsavel',
          'responsavelTecnico',
          'agronomo',
          'data',
        ])

        this.detailModal.item = {
          ...technician,
          ...detail,
        }
        this.detailModal.subtitle =
          this.detailModal.item.crea ||
          this.detailModal.item.documento ||
          this.detailModal.item.cpf ||
          'Cadastro de responsável técnico'
      } catch (error) {
        this.showToast(this.getErrorMessage(error), 'warning')
      } finally {
        this.detailModal.loading = false
      }
    },
    getRecordId(record, keys) {
      const key = keys.find((itemKey) => record && record[itemKey] !== undefined && record[itemKey] !== null)
      return key ? record[key] : ''
    },
    extractDetailItem(data, preferredKeys) {
      if (!data || typeof data !== 'object') return {}

      const key = preferredKeys.find(
        (itemKey) =>
          data[itemKey] &&
          typeof data[itemKey] === 'object' &&
          !Array.isArray(data[itemKey])
      )

      if (key) return data[key]

      return data
    },
    resetClientDetails(clearClient = true) {
      if (clearClient) {
        this.selectedClient = null
      }
      this.clientProducers = []
      this.resetProducersPagination()
      this.clientTechnicians = []
      this.technicianSearch = ''
      this.clientPrescriptions = []
      this.logoPreview = ''
      this.clientSettings = {
        ambiente: 'producao',
        sincronizacaoAtiva: true,
        emitirReceitasAutomaticamente: false,
        exigirResponsavelTecnico: false,
        observacoes: '',
        unidadeRecebimentoEmbalagens: '',
      }
    },
    statusLabel(status) {
      const labels = {
        ativo: 'Sincronizado',
        pendente: 'Atenção',
        erro: 'Erro',
        inativo: 'Inativo',
      }

      return labels[status] || 'Indefinido'
    },
    simpleStatusLabel(status) {
      const labels = {
        ativo: 'Ativo',
        inativo: 'Inativo',
        pendente: 'Pendente',
        erro: 'Erro',
      }

      return labels[status] || status
    },
    shouldShowDetailField(key, value, item = {}) {
      if (value === undefined || value === null || value === '') return false

      const normalizedKey = String(key || '')
      const lowerKey = normalizedKey.toLowerCase()
      const isIdField =
        lowerKey === 'id' ||
        lowerKey.startsWith('id_') ||
        /^id[A-Z]/.test(normalizedKey)

      if (isIdField) return false

      if (normalizedKey === 'municipio' && this.sameText(value, item.cidade)) {
        return false
      }

      if (normalizedKey === 'estadoSigla' && item.estado) {
        return false
      }

      return true
    },
    sameText(firstValue, secondValue) {
      return String(firstValue || '')
        .trim()
        .toLowerCase() === String(secondValue || '').trim().toLowerCase()
    },
    fieldLabel(key) {
      const labels = {
        id: 'Código',
        id_produtor_rural: 'Código do produtor',
        id_agronomo: 'Código do responsável',
        nome: 'Nome',
        documento: 'Documento',
        cpf: 'CPF',
        cnpj: 'CNPJ',
        cpfCnpj: 'CPF/CNPJ',
        cpf_cnpj: 'CPF/CNPJ',
        inscricaoEstadual: 'Inscrição estadual',
        inscricao_estadual: 'Inscrição estadual',
        propriedade: 'Propriedade',
        endereco: 'Endereço',
        logradouro: 'Logradouro',
        numero: 'Número',
        complemento: 'Complemento',
        bairro: 'Bairro',
        cep: 'CEP',
        cidade: 'Cidade',
        municipio: 'Município',
        estado: 'Estado',
        uf: 'UF',
        email: 'E-mail',
        telefone: 'Telefone',
        status: 'Status',
        ativo: 'Ativo',
        totalReceitas: 'Total de receitas',
        crea: 'CREA',
        profissao: 'Profissão',
        n_receitas_art: 'Número de receitas ART',
        nReceitasArt: 'Número de receitas ART',
        nReceitasMes: 'Receitas no mês',
        receita_art_disponivel: 'Receitas ART disponíveis',
        receitaArtDisponivel: 'Receitas ART disponíveis',
        data_cadastro: 'Data de cadastro',
        dataCadastro: 'Data de cadastro',
        data_atualizacao: 'Data de atualização',
        dataAtualizacao: 'Data de atualização',
        ultima_receita: 'Última receita',
        ultimaReceita: 'Última receita',
        estadoSigla: 'UF',
        buscaArt: 'Busca ART',
        artPrimaria: 'ART primária',
        artSecundaria: 'ART secundária',
        formatoReceita: 'Formato da receita',
        nCopias: 'Número de cópias',
        registroSc: 'Registro SC',
        registroEmpresaSc: 'Registro da empresa SC',
        digitalSignature: 'Assinatura digital',
        isSignProd: 'Assinatura do produtor',
        localAplicacao: 'Local de aplicação',
      }

      if (labels[key]) return labels[key]

      return String(key)
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (letter) => letter.toUpperCase())
    },
    formatDetailValue(key, value) {
      if (typeof value === 'boolean') {
        return value ? 'Sim' : 'Não'
      }

      if (key === 'status') {
        return this.simpleStatusLabel(value)
      }

      if (key === 'estado') {
        const stateCode = this.detailModal.item && this.detailModal.item.estadoSigla
        return stateCode ? `${value} (${stateCode})` : String(value)
      }

      if (key.toLowerCase().includes('data') || key === 'ultima_receita' || key === 'ultimaReceita') {
        return this.formatDateTime(value)
      }

      if (Array.isArray(value)) {
        return value.length ? JSON.stringify(value) : '-'
      }

      if (typeof value === 'object') {
        return JSON.stringify(value)
      }

      return String(value)
    },
    initials(name) {
      return String(name || '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join('')
        .toUpperCase()
    },
    async handleLogoUpload(event) {
      const file = event && event.target && event.target.files
        ? event.target.files[0]
        : null

      if (!file || !this.selectedClient) return

      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        this.showToast('Use uma imagem JPG, JPEG, PNG ou WEBP.', 'warning')
        event.target.value = ''
        return
      }

      if (file.size > 2 * 1024 * 1024) {
        this.showToast('A logo deve ter no maximo 2MB.', 'warning')
        event.target.value = ''
        return
      }

      this.isDetailLoading = true

      try {
        const response = await integrationClientsService.uploadIntegrationClientLogo(
          this.token,
          this.selectedClient.id,
          file
        )
        this.logoPreview = response.data.logoUrl || this.logoPreview
        this.$set(this.selectedClient, 'logoUrl', this.logoPreview)
        this.showToast(response.data.message || 'Logo atualizada com sucesso.', 'success')
      } catch (error) {
        this.showToast(this.getErrorMessage(error), 'danger')
      } finally {
        this.isDetailLoading = false
        event.target.value = ''
      }
    },
    async saveSettings() {
      if (!this.selectedClient) return

      this.isDetailLoading = true

      try {
        const response = await integrationClientsService.updateIntegrationClientSettings(
          this.token,
          this.selectedClient.id,
          this.clientSettings
        )
        this.clientSettings = {
          ...this.clientSettings,
          ...(response.data.configuracoes || {}),
        }
        this.showToast(response.data.message || 'Configurações atualizadas com sucesso.', 'success')
      } catch (error) {
        this.showToast(this.getErrorMessage(error), 'danger')
      } finally {
        this.isDetailLoading = false
      }
    },
    showBackendPending() {
      this.showToast('Ação ainda depende de endpoint específico do backend.', 'info')
    },
    showToast(message, variant = 'info') {
      this.$bvToast.toast(message, {
        title: 'Painel de integração',
        variant,
        solid: true,
      })
    },
    getErrorMessage(error) {
      const data = error && error.response && error.response.data
      if (data && data.aviso && data.aviso.mensagem) {
        return data.aviso.mensagem
      }

      return error && error.message
        ? error.message
        : 'Não foi possível concluir a operação.'
    },
    formatDate(value) {
      if (!value) return '-'

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value

      return date.toLocaleDateString('pt-BR')
    },
    formatDateTime(value) {
      if (!value) return '-'

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value

      return date.toLocaleString('pt-BR')
    },
    formatMoney(value) {
      if (value === undefined || value === null || value === '') return '-'

      return Number(value).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.integration-clients {
  display: grid;
  gap: 18px;
  padding: 24px;
}

.clients-hero {
  align-items: flex-start;
  background: #fff;
  border: 1px solid #dfe6ef;
  border-radius: 8px;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  padding: 24px;

  h1 {
    color: #172033;
    font-size: 26px;
    font-weight: 800;
    margin: 4px 0 6px;
  }

  p {
    color: #647084;
    margin: 0;
  }
}

.hero-eyebrow {
  color: #008e75;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.hero-actions,
.detail-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;

  .btn {
    align-items: center;
    border-radius: 5px;
    display: inline-flex;
    font-weight: 700;
    gap: 7px;
    min-height: 40px;
    padding: 0 16px;
  }
}

.btn-webreceita {
  background: #008e75;
  border-color: #008e75;
  color: #fff;
}

.summary-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.summary-card {
  background: #fff;
  border: 1px solid #dfe6ef;
  border-radius: 8px;
  display: grid;
  gap: 7px;
  min-height: 112px;
  padding: 18px;

  span {
    color: #172033;
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
  }

  strong {
    color: #172033;
    font-size: 25px;
    font-weight: 800;
    line-height: 1;
  }

  small {
    color: #647084;
  }

  &.is-success {
    border-color: rgba(0, 142, 117, 0.6);

    strong {
      color: #008e75;
    }
  }

  &.is-warning {
    border-color: rgba(246, 184, 76, 0.8);

    strong {
      color: #b7791f;
    }
  }

  &.is-danger {
    border-color: rgba(220, 53, 69, 0.65);

    strong {
      color: #dc3545;
    }
  }
}

.manager-card {
  background: #fff;
  border: 1px solid #dfe6ef;
  border-radius: 8px;
  overflow: hidden;
}

.toolbar {
  align-items: center;
  background: #f7f9fc;
  border-bottom: 1px solid #e7edf4;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(260px, 560px) max-content;
  padding: 16px;

  select {
    background: #fff;
    border: 1px solid #d8dee8;
    border-radius: 5px;
    color: #172033;
    height: 40px;
    padding: 0 12px;
  }
}

.toolbar-search-button {
  align-items: center;
  border-radius: 5px;
  display: inline-flex;
  font-weight: 800;
  gap: 7px;
  height: 40px;
  justify-content: center;
  min-width: 128px;
  padding: 0 16px;
  width: auto;
}

.search-box {
  align-items: center;
  background: #fff;
  border: 1px solid #d8dee8;
  border-radius: 5px;
  display: flex;
  gap: 8px;
  height: 40px;
  padding: 0 12px;

  i {
    color: #647084;
  }

  input {
    border: 0;
    flex: 1;
    min-width: 0;
    outline: none;
  }
}

.content-grid {
  display: grid;
  grid-template-columns: minmax(320px, 0.8fr) minmax(520px, 1.2fr);
  min-height: 520px;
}

.client-list {
  border-right: 1px solid #e7edf4;
}

.client-row {
  align-items: center;
  background: #fff;
  border: 0;
  border-bottom: 1px solid #e7edf4;
  display: grid;
  gap: 12px;
  grid-template-columns: 10px minmax(0, 1fr) auto;
  padding: 16px 18px;
  text-align: left;
  width: 100%;

  &.active,
  &:hover {
    background: #f5fbf9;
  }
}

.client-status-dot {
  border-radius: 50%;
  height: 10px;
  width: 10px;

  &.ativo {
    background: #008e75;
  }

  &.pendente {
    background: #f6b84c;
  }

  &.erro {
    background: #dc3545;
  }

  &.inativo {
    background: #8b95a7;
  }
}

.client-main {
  min-width: 0;

  strong,
  small {
    display: block;
  }

  strong {
    color: #172033;
    font-weight: 800;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    color: #647084;
    margin-top: 3px;
  }
}

.client-status-pill {
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  padding: 5px 9px;

  &.ativo {
    background: #e7f5ef;
    color: #008e75;
  }

  &.pendente {
    background: #fff5dd;
    color: #986915;
  }

  &.erro {
    background: #ffe9ed;
    color: #dc3545;
  }

  &.inativo {
    background: #eef2f7;
    color: #536074;
  }
}

.detail-panel {
  padding: 22px;
}

.detail-head {
  align-items: center;
  display: flex;
  gap: 12px;
  margin-bottom: 18px;

  h2 {
    color: #172033;
    font-size: 19px;
    font-weight: 800;
    margin: 0 0 3px;
  }

  p {
    color: #647084;
    margin: 0;
  }
}

.detail-avatar {
  align-items: center;
  background: #dff6f3;
  border-radius: 50%;
  color: #008e75;
  display: inline-flex;
  flex: 0 0 44px;
  font-weight: 800;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.client-tabs {
  border-bottom: 1px solid #e7edf4;
  display: flex;
  gap: 6px;
  margin-bottom: 18px;
  overflow-x: auto;
  padding-bottom: 10px;

  button {
    align-items: center;
    background: #f7f9fc;
    border: 1px solid #e2e9f2;
    border-radius: 999px;
    color: #536074;
    display: inline-flex;
    flex: 0 0 auto;
    font-size: 12px;
    font-weight: 800;
    gap: 6px;
    padding: 8px 11px;
  }

  button.active {
    background: #e7f5ef;
    border-color: rgba(0, 142, 117, 0.45);
    color: #008e75;
  }
}

.tab-panel {
  display: grid;
  gap: 16px;
}

.panel-head {
  align-items: flex-start;
  display: flex;
  gap: 14px;
  justify-content: space-between;

  h3 {
    color: #172033;
    font-size: 17px;
    font-weight: 800;
    margin: 0 0 4px;
  }

  p {
    color: #647084;
    margin: 0;
  }

  .btn {
    align-items: center;
    display: inline-flex;
    gap: 7px;
    white-space: nowrap;
  }
}

.detail-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  div {
    background: #f7f9fc;
    border: 1px solid #e7edf4;
    border-radius: 7px;
    padding: 12px;
  }

  span,
  strong {
    display: block;
  }

  span {
    color: #647084;
    font-size: 12px;
    font-weight: 700;
  }

  strong {
    color: #172033;
    margin-top: 5px;
  }
}

.producer-toolbar {
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(220px, 1fr);
}

.health-box,
.pending-box {
  border-top: 1px solid #e7edf4;
  margin-top: 18px;
  padding-top: 18px;
}

.health-box {
  span {
    color: #647084;
    display: block;
    font-size: 12px;
    font-weight: 700;
  }

  strong {
    color: #008e75;
    display: block;
    font-size: 24px;
    font-weight: 800;
    margin-top: 4px;
  }
}

.health-bar {
  background: #e7edf4;
  border-radius: 999px;
  height: 8px;
  margin-top: 10px;
  overflow: hidden;

  span {
    background: #008e75;
    border-radius: inherit;
    display: block;
    height: 100%;
  }
}

.pending-box {
  h3 {
    color: #172033;
    font-size: 15px;
    font-weight: 800;
    margin: 0 0 10px;
  }

  ul {
    color: #536074;
    margin: 0;
    padding-left: 18px;
  }

  li + li {
    margin-top: 6px;
  }
}

.detail-actions {
  border-top: 1px solid #e7edf4;
  justify-content: flex-start;
  margin-top: 18px;
  padding-top: 18px;
}

.mini-table {
  border: 1px solid #e7edf4;
  border-radius: 8px;
  overflow: hidden;
}

.mini-row {
  background: #fff;
  display: grid;
  gap: 3px;
  padding: 13px 15px;

  & + & {
    border-top: 1px solid #e7edf4;
  }

  strong {
    color: #172033;
    font-weight: 800;
  }

  span,
  small {
    color: #647084;
  }
}

.person-row {
  align-items: center;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr) auto;

  > div {
    display: grid;
    gap: 3px;
    min-width: 0;
  }
}

.btn-row-action {
  align-items: center;
  border-radius: 5px;
  display: inline-flex;
  font-size: 13px;
  font-weight: 800;
  gap: 6px;
  min-height: 34px;
  padding: 0 11px;
  white-space: nowrap;
}

.mini-empty {
  color: #647084;
  padding: 18px;
  text-align: center;
}

.mini-pagination {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;

  span {
    color: #647084;
    font-size: 13px;
    font-weight: 700;
  }

  div {
    display: flex;
    gap: 8px;
  }

  .btn {
    align-items: center;
    border-radius: 5px;
    display: inline-flex;
    font-weight: 700;
    gap: 6px;
    min-height: 36px;
    padding: 0 12px;
  }
}

.recipe-row {
  grid-template-columns: 150px minmax(0, 1fr) auto;
  align-items: center;

  small {
    justify-self: end;
  }
}

.settings-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  label {
    color: #536074;
    display: grid;
    font-size: 12px;
    font-weight: 800;
    gap: 7px;
  }

  input,
  select,
  textarea {
    background: #fff;
    border: 1px solid #d8dee8;
    border-radius: 5px;
    color: #172033;
    font-size: 14px;
    outline: none;
  }

  input,
  select {
    height: 40px;
    padding: 0 12px;
  }

  textarea {
    min-height: 82px;
    padding: 10px 12px;
    resize: vertical;
  }
}

.settings-wide {
  grid-column: 1 / -1;
}

.logo-upload-card {
  align-items: center;
  background: #f7f9fc;
  border: 1px solid #e7edf4;
  border-radius: 8px;
  display: grid;
  gap: 16px;
  grid-template-columns: 88px minmax(0, 1fr);
  padding: 16px;

  strong {
    color: #172033;
    display: block;
    font-weight: 800;
    margin-bottom: 4px;
  }

  p {
    color: #647084;
    margin: 0 0 12px;
  }
}

.logo-preview {
  align-items: center;
  background: #fff;
  border: 1px solid #dfe6ef;
  border-radius: 8px;
  color: #008e75;
  display: inline-flex;
  font-weight: 800;
  height: 82px;
  justify-content: center;
  overflow: hidden;
  width: 82px;

  img {
    height: 100%;
    object-fit: contain;
    width: 100%;
  }
}

.logo-upload-button {
  align-items: center;
  background: #fff;
  border: 1px solid #d8dee8;
  border-radius: 5px;
  color: #172033;
  cursor: pointer;
  display: inline-flex;
  font-weight: 800;
  gap: 7px;
  min-height: 38px;
  margin: 0;
  padding: 0 13px;

  input {
    display: none;
  }
}

.empty-state {
  color: #647084;
  padding: 28px;
  text-align: center;
}

.record-detail-modal {
  display: grid;
  gap: 16px;
}

.record-detail-head {
  align-items: center;
  border-bottom: 1px solid #e7edf4;
  display: flex;
  gap: 12px;
  padding-bottom: 14px;

  h3 {
    color: #172033;
    font-size: 18px;
    font-weight: 800;
    margin: 0 0 3px;
  }

  p {
    color: #647084;
    margin: 0;
  }
}

.record-summary-strip {
  background: #f7f9fc;
  border: 1px solid #e7edf4;
  border-radius: 7px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  overflow: hidden;

  div {
    display: grid;
    gap: 4px;
    min-width: 0;
    padding: 12px 14px;

    & + div {
      border-left: 1px solid #e7edf4;
    }
  }

  span {
    color: #647084;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
  }

  strong {
    color: #172033;
    font-size: 15px;
    line-height: 1.25;
    overflow-wrap: anywhere;
  }
}

.record-sections {
  display: grid;
  gap: 14px;
}

.record-section {
  border: 1px solid #e7edf4;
  border-radius: 7px;
  overflow: hidden;

  h4 {
    background: #fbfcfe;
    border-bottom: 1px solid #e7edf4;
    color: #172033;
    font-size: 13px;
    font-weight: 800;
    margin: 0;
    padding: 11px 14px;
  }

  dl {
    display: grid;
    grid-template-columns: minmax(150px, 0.45fr) minmax(0, 1fr);
    margin: 0;
  }

  dt,
  dd {
    border-top: 1px solid #eef2f7;
    margin: 0;
    min-width: 0;
    padding: 10px 14px;
  }

  dt:nth-of-type(1),
  dd:nth-of-type(1) {
    border-top: 0;
  }

  dt {
    color: #647084;
    font-size: 12px;
    font-weight: 800;
  }

  dd {
    color: #172033;
    font-weight: 700;
    overflow-wrap: anywhere;
  }
}

@media (max-width: 1100px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .content-grid,
  .toolbar {
    grid-template-columns: 1fr;
  }

  .client-list {
    border-right: 0;
  }
}

@media (max-width: 768px) {
  .integration-clients {
    padding: 16px;
  }

  .clients-hero,
  .hero-actions,
  .detail-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .summary-grid,
  .detail-grid,
  .settings-grid,
  .logo-upload-card,
  .recipe-row,
  .person-row,
  .record-summary-strip,
  .record-section dl {
    grid-template-columns: 1fr;
  }

  .panel-head {
    flex-direction: column;
  }

  .recipe-row small {
    justify-self: start;
  }

  .client-row {
    grid-template-columns: 10px minmax(0, 1fr);
  }

  .client-status-pill {
    grid-column: 2;
    justify-self: flex-start;
  }

  .btn-row-action {
    justify-content: center;
    width: 100%;
  }

  .record-summary-strip div + div {
    border-left: 0;
    border-top: 1px solid #e7edf4;
  }

  .record-section dd {
    border-top: 0;
    padding-top: 0;
  }
}
</style>
